import type { ButtonHTMLAttributes, FC } from "react";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";
import clsx from "clsx";

const buttonStyles = tv({
	base: [
		"relative w-full h-12 px-4 rounded-lg font-medium text-sm text-center",
		"transition-colors duration-200",
		"disabled:opacity-50 disabled:cursor-not-allowed",
		"cursor-pointer",
		"focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
	],
	variants: {
		variant: {
			primary: [
				"bg-brand-primary text-white",
				"border border-brand-primary-900",
				"hover:border-brand-primary-900 hover:bg-brand-primary-900"
			],
			outline: [
				"border-brand-dark/10 bg-brand-light-raised text-brand-dark-900",
				"border border-brand-dark/10",
				"hover:bg-brand-dark/5"
			]
		},

		// "md" is the full-width form button the base already describes, so it stays empty and
		// every existing call site keeps rendering exactly as before.
		size: {
			md: "",
			sm: "h-9 w-auto px-3"
		},

		// Declared after `size` so it wins when both are set — tailwind-variants runs its own
		// base and variant classes through tailwind-merge in declaration order.
		iconOnly: {
			true: "size-9 px-0",
			false: ""
		}
	},
	defaultVariants: {
		variant: "primary",
		size: "md",
		iconOnly: false
	}
});

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
	VariantProps<typeof buttonStyles> & {
		loading?: boolean;
	};

const Spinner: FC = () => {
	return (
		<svg
			className="animate-spin"
			width={20}
			height={20}
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden="true"
		>
			<circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
			<path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
		</svg>
	);
};

export const Button: FC<ButtonProps> = ({
	children,
	className,
	variant,
	size,
	iconOnly,
	loading = false,
	disabled,
	...props
}) => {
	return (
		<button
			// `className` goes through tv rather than clsx so a caller's utility actually
			// overrides the base one it conflicts with instead of merely following it.
			className={buttonStyles({ variant, size, iconOnly, className })}
			disabled={disabled || loading}
			aria-busy={loading || undefined}
			{...props}
		>
			<span className={clsx("inline-flex items-center justify-center gap-2", loading && "invisible")}>
				{children}
			</span>
			{loading && (
				<span className="absolute inset-0 flex items-center justify-center">
					<Spinner />
				</span>
			)}
		</button>
	);
};
